import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ReservationAdminService } from '../../../_services/api/reservation-admin.service';
import { OfferAgentService } from '../../../_services/api/offer-agents.service';
import { OfferUtilsService } from '../../../_utils/offer-utils.service';
import { ReservationDetail, OfferCreate, Offer, Agent } from '../../../_models/reservation.model';
import { AvailableAgentsResponse, AgentSimple } from '../../../_models/agent.model';
import { CommonModule } from '@angular/common';
import { PriceFormatPipe } from '../../../_pipes/price_format.pipe';

@Component({
  selector: 'app-admin-offer-form',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, PriceFormatPipe],
  templateUrl: './admin-offer-form.component.html',
  styleUrls: ['./admin-offer-form.component.scss']
})
export class AdminOfferFormComponent implements OnInit, OnDestroy {
  reservationId!: number;
  offerId?: number;
  isEditMode = false;
  reservation!: ReservationDetail;
  
  offerForm!: FormGroup;
  availableAgents: AgentSimple[] = [];
  filteredAgents: AgentSimple[] = [];
  selectedAgentIds: number[] = [];
  
  isLoading = false;
  isSubmitting = false;
  errorMessage = '';
  successMessage = '';
  
  searchTerm = '';
  specializationFilter = '';
  specializations: string[] = [];
  
  private validationMessages = {
    price: {
      required: 'Le prix est obligatoire',
      min: 'Le prix doit être supérieur à 0',
      pattern: 'Le prix doit être un nombre valide'
    },
    description: {
      required: 'La description est obligatoire',
      minlength: 'La description doit contenir au moins 10 caractères',
      maxlength: 'La description ne peut pas dépasser 500 caractères'
    },
    agentIds: {
      required: 'Au moins un agent doit être sélectionné',
      maxlength: (max: number) => `Vous ne pouvez sélectionner que ${max} agents maximum`
    }
  };
  
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private reservationAdminService: ReservationAdminService,
    private agentReservationService: OfferAgentService,
    private offerUtilsService: OfferUtilsService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    
    this.getRouteParams();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.offerForm = this.fb.group({
      price: ['', [Validators.required, Validators.min(0.01), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      description: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(500)]],
      agentIds: [[], [Validators.required]]
    });
  }

  private getRouteParams(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.reservationId = +params['reservationId'];
      this.offerId = params['offerId'] ? +params['offerId'] : undefined;
      this.isEditMode = !!this.offerId;
      
      if (this.reservationId) {
        this.loadReservationData();
      } else {
        this.errorMessage = 'ID de réservation invalide';
        this.router.navigate(['/admin/reservations']);
      }
    });
  }

  private loadReservationData(): void {
    this.isLoading = true;
    this.errorMessage = '';
    
    this.reservationAdminService.getReservationDetails(this.reservationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.reservation = response.data;
          if(!this.offerUtilsService.canCreateOfferForReservation(this.reservation.reservationAdminStatus)){
            this.router.navigate(['/admin/reservations/details', this.reservationId]);
          }
          
          this.offerForm.get('agentIds')?.setValidators([
            Validators.required,
            Validators.maxLength(this.reservation.requiredAgents)
          ]);
          this.offerForm.get('agentIds')?.updateValueAndValidity();
          
          if (this.isEditMode && this.offerId) {
            this.loadOfferData();
          } else {
            this.loadAvailableAgents();
          }
        },
        error: (error) => {
          this.errorMessage = 'Erreur lors du chargement de la réservation: ' + 
            (error.error?.message || 'Veuillez réessayer');
          this.isLoading = false;
        }
      });
  }

  private loadOfferData(): void {
    if (!this.offerId) return;
    
    const offer = this.reservation.offers.find(o => o.id === this.offerId);
    if (!offer) {
      this.errorMessage = 'Offre non trouvée';
      this.isLoading = false;
      return;
    }
    
    if (!this.offerUtilsService.canEditOffer(offer)) {
      this.errorMessage = 'Cette offre ne peut pas être modifiée';
      this.isLoading = false;
      return;
    }
    
    this.offerForm.patchValue({
      price: offer.price,
      description: offer.description,
      agentIds: offer.agents.map(agent => agent.id)
    });
    
    this.selectedAgentIds = [...offer.agents.map(agent => agent.id)];
    this.loadAvailableAgents();
  }

  private loadAvailableAgents(): void {
    this.agentReservationService.getAvailableAgents(this.reservationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.availableAgents = response.data.availableAgents;
          this.filteredAgents = [...this.availableAgents];
          
          // Extract unique specializations
          this.specializations = [...new Set(
            this.availableAgents
              .map(agent => agent.specialization)
              .filter(s => s)
          )];
          
          this.isLoading = false;
        },
        error: (error) => {
          this.errorMessage = 'Erreur lors du chargement des agents disponibles: ' + 
            (error.error?.message || 'Veuillez réessayer');
          this.isLoading = false;
        }
      });
  }

  filterAgents(): void {
    this.filteredAgents = this.availableAgents.filter(agent => {
      const matchesSearch = !this.searchTerm || 
        agent.fullName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        agent.email.toLowerCase().includes(this.searchTerm.toLowerCase());
      
      const matchesSpecialization = !this.specializationFilter || 
        agent.specialization === this.specializationFilter;
      
      return matchesSearch && matchesSpecialization;
    });
  }

  toggleAgentSelection(agentId: number): void {
    const index = this.selectedAgentIds.indexOf(agentId);
    
    if (index > -1) {
      this.selectedAgentIds.splice(index, 1);
    } else {
      if (this.selectedAgentIds.length >= this.reservation.requiredAgents) {
        return; // Cannot select more agents than required
      }
      this.selectedAgentIds.push(agentId);
    }
    
    this.offerForm.get('agentIds')?.setValue([...this.selectedAgentIds]);
    this.offerForm.get('agentIds')?.markAsTouched();
  }

  isAgentSelected(agentId: number): boolean {
    return this.selectedAgentIds.includes(agentId);
  }

  getSelectedAgents(): AgentSimple[] {
    return this.availableAgents.filter(agent => 
      this.selectedAgentIds.includes(agent.id)
    );
  }

  getAgentDisplayInfo(agent: AgentSimple): string {
    return `${agent.fullName} | ${agent.specialization || 'Non spécifié'} | ${agent.rating}/5 ⭐`;
  }

  get isMaxAgentsSelected(): boolean {
    return this.selectedAgentIds.length >= this.reservation.requiredAgents;
  }

  get remainingAgents(): number {
    return Math.max(0, this.reservation.requiredAgents - this.selectedAgentIds.length);
  }

  onSubmit(): void {
    if (this.offerForm.invalid || this.isSubmitting) {
      this.markFormGroupTouched(this.offerForm);
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    const offerData: OfferCreate = {
      price: this.offerForm.value.price,
      description: this.offerForm.value.description,
      agentIds: this.offerForm.value.agentIds
    };

    if (this.isEditMode && this.offerId) {
      this.updateOffer(offerData);
    } else {
      this.createOffer(offerData);
    }
  }

  private createOffer(offerData: OfferCreate): void {
    this.reservationAdminService.createOffer(this.reservationId, offerData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.successMessage = 'Offre créée avec succès!';
          setTimeout(() => {
            this.router.navigate(['/admin/reservations/details', this.reservationId]);
          }, 1500);
        },
        error: (error) => {
          this.errorMessage = 'Erreur lors de la création de l\'offre: ' + 
            (error.error?.message || 'Veuillez réessayer');
          this.isSubmitting = false;
        }
      });
  }

  private updateOffer(offerData: OfferCreate): void {
    if (!this.offerId) return;
    
    this.reservationAdminService.updateOffer(this.offerId, offerData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.successMessage = 'Offre modifiée avec succès!';
          setTimeout(() => {
            this.router.navigate(['/admin/reservations/details', this.reservationId]);
          }, 1500);
        },
        error: (error) => {
          this.errorMessage = 'Erreur lors de la modification de l\'offre: ' + 
            (error.error?.message || 'Veuillez réessayer');
          this.isSubmitting = false;
        }
      });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getErrorMessage(controlName: string): string {
    const control = this.offerForm.get(controlName);
    
    if (!control || !control.errors || !control.touched) {
      return '';
    }
    const errors = control.errors;
    
    if (controlName === 'price') {
      if (errors['required']) {
        return this.validationMessages.price.required;
      }
      if (errors['min']) {
        return this.validationMessages.price.min;
      }
      if (errors['pattern']) {
        return this.validationMessages.price.pattern;
      }
    }
    
    if (controlName === 'description') {
      if (errors['required']) {
        return this.validationMessages.description.required;
      }
      if (errors['minlength']) {
        return this.validationMessages.description.minlength;
      }
      if (errors['maxlength']) {
        return this.validationMessages.description.maxlength;
      }
    }
    
    if (controlName === 'agentIds') {
      if (errors['required']) {
        return this.validationMessages.agentIds.required;
      }
      if (errors['maxlength']) {
        const max = errors['maxlength']?.requiredLength || this.reservation.requiredAgents;
        return this.validationMessages.agentIds.maxlength(max);
      }
    }
    
    return 'Valeur invalide';
  }

  onCancel(): void {
    this.router.navigate(['/admin/reservations/details', this.reservationId]);
  }

  formatAddress(address: any): string {
    if (!address) return 'Non spécifiée';
    
    const parts: string[] = [];
    
    if (address.streetNumber && address.street) {
      parts.push(`${address.street} ${address.streetNumber}`);
    } else if (address.street) {
      parts.push(address.street);
    }
    
    if (address.postalCode && address.city) {
      parts.push(`${address.postalCode} ${address.city}`);
    } else if (address.postalCode) {
      parts.push(address.postalCode);
    } else if (address.city) {
      parts.push(address.city);
    }
    
    if (parts.length === 0) {
      return 'Non spécifiée';
    }
    
    return parts.join(', ') + ".";
  }
}